/**
 * Projects Block JavaScript
 */

import { isBlockOnPage } from "../../src/js/modules/block-present.js";

(function () {
  "use strict";

  if (!isBlockOnPage("projects")) {
    return;
  }

  function getAjaxUrl(resultsEl) {
    const dataUrl = resultsEl.dataset.ajaxUrl;
    if (dataUrl) {
      return dataUrl;
    }
    if (window.sojTheme && window.sojTheme.ajaxUrl) {
      return window.sojTheme.ajaxUrl;
    }
    return "/wp-admin/admin-ajax.php";
  }

  function initCustomSelect(container) {
    const select = container.querySelector("select");
    const trigger = container.querySelector(".custom-select-trigger");
    const label = container.querySelector(".custom-select-label");
    const dropdown = container.querySelector(".custom-select-dropdown");

    if (!select || !trigger || !dropdown) return;

    function getSelectedLabel() {
      const opt = select.options[select.selectedIndex];
      return opt ? opt.textContent.trim() : "";
    }

    function getDisplayLabel() {
      const selected = getSelectedLabel();
      if (
        select.value === "" &&
        container.dataset.placeholderShort &&
        window.innerWidth < 1024
      ) {
        return container.dataset.placeholderShort;
      }
      return selected;
    }

    function buildOptions() {
      dropdown.innerHTML = "";
      const selectedIndex = select.selectedIndex;
      Array.from(select.options).forEach((opt, index) => {
        if (opt.hasAttribute("data-dropdown-placeholder")) {
          return;
        }
        const div = document.createElement("div");
        div.className = "custom-select-option";
        div.setAttribute("role", "option");
        div.setAttribute("data-value", opt.value);
        div.textContent = opt.textContent.trim();
        if (index === selectedIndex) {
          div.setAttribute("aria-selected", "true");
        }
        dropdown.appendChild(div);
      });
    }

    function open() {
      container.classList.add("is-open");
      trigger.setAttribute("aria-expanded", "true");
      dropdown.hidden = false;
    }

    function close() {
      container.classList.remove("is-open");
      trigger.setAttribute("aria-expanded", "false");
      dropdown.hidden = true;
    }

    function choose(value) {
      select.value = value;
      label.textContent = getDisplayLabel();
      buildOptions();
      close();
      select.dispatchEvent(new Event("change", { bubbles: true }));
    }

    function updateLabel() {
      label.textContent = getDisplayLabel();
    }

    function syncFromSelect() {
      updateLabel();
      buildOptions();
    }

    label.textContent = getDisplayLabel();
    buildOptions();

    trigger.addEventListener("click", (e) => {
      e.preventDefault();
      if (container.classList.contains("is-open")) {
        close();
      } else {
        open();
      }
    });

    dropdown.addEventListener("click", (e) => {
      const option = e.target.closest(".custom-select-option");
      if (option) {
        e.preventDefault();
        choose(option.getAttribute("data-value"));
      }
    });

    document.addEventListener("click", (e) => {
      if (!container.contains(e.target)) {
        close();
      }
    });

    document.addEventListener("soj-update-filter-labels", syncFromSelect);
  }

  function initProjects() {
    const blocks = document.querySelectorAll(".projects");

    blocks.forEach((block) => {
      block.querySelectorAll("[data-custom-select]").forEach(initCustomSelect);

      const results = block.querySelector(".projects__results");
      const list = results
        ? results.querySelector("[data-projects-columns]")
        : null;
      const spaceSelect = block.querySelector(
        '.projects-filter-select[data-filter="space"]',
      );
      const styleSelect = block.querySelector(
        '.projects-filter-select[data-filter="style"]',
      );
      const collectionSelect = block.querySelector(
        '.projects-filter-select[data-filter="collection"]',
      );
      const clearFiltersButton = block.querySelector(".clear-filters-button");

      if (
        !results ||
        !list ||
        (!spaceSelect && !styleSelect && !collectionSelect)
      ) {
        return;
      }

      const nonce = results.dataset.nonce;
      const action = results.dataset.action || "soj_filter_projects";
      const ajaxUrl = getAjaxUrl(results);
      const initialFilterValues = {
        space: spaceSelect ? spaceSelect.value : "",
        style: styleSelect ? styleSelect.value : "",
        collection: collectionSelect ? collectionSelect.value : "",
      };
      let isResettingFilters = false;

      const emptyText =
        results.dataset.emptyText || "No projects found at the moment.";

      const setEmptyState = (hasResults) => {
        let emptyMessage = results.querySelector("[data-empty-message]");
        if (hasResults) {
          if (emptyMessage) {
            emptyMessage.remove();
          }
          return;
        }
        if (!emptyMessage) {
          emptyMessage = document.createElement("p");
          emptyMessage.className = "projects__empty";
          emptyMessage.setAttribute("data-empty-message", "");
          emptyMessage.textContent = emptyText;
          list.after(emptyMessage);
        }
      };
      const updateClearButtonVisibility = () => {
        if (!clearFiltersButton) {
          return;
        }

        const hasAnyFilter =
          (spaceSelect && spaceSelect.value !== "") ||
          (styleSelect && styleSelect.value !== "") ||
          (collectionSelect && collectionSelect.value !== "");

        clearFiltersButton.classList.toggle("is-visible", !!hasAnyFilter);
      };

      const gridEl = list;
      let parallaxState = null;
      let parallaxTicking = false;
      const parallaxEnabled = block.dataset.parallax !== "false";
      const useContainer = block.dataset.container !== "false";
      const parallaxMinWidth = 768;
      const prefersReducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      ).matches;

      function clearParallaxStyles() {
        if (!gridEl) return;
        gridEl.style.marginBottom = "";
        const items = gridEl.querySelectorAll(".projects__item");
        items.forEach((item) => {
          item.style.transform = "";
        });
      }

      function updateParallaxLayout() {
        if (!parallaxEnabled || prefersReducedMotion) return;
        const width =
          window.innerWidth || document.documentElement.clientWidth || 0;
        if (!gridEl) return;

        const items = gridEl.querySelectorAll(".projects__item");
        if (!items.length) {
          parallaxState = null;
          clearParallaxStyles();
          return;
        }

        const numColumns =
          !useContainer && width > 1920 && items.length > 12
            ? 4
            : width >= 1024
              ? 3
              : width >= 768
                ? 2
                : 1;
        const rect = gridEl.getBoundingClientRect();
        const scrollY = window.scrollY || window.pageYOffset || 0;
        const sectionTop = rect.top + scrollY;

        const columnSpeeds = [];
        for (let i = 0; i < numColumns; i++) {
          if (numColumns === 2) {
            columnSpeeds.push(i === 0 ? 0.25 : 0.15);
          } else if (numColumns === 3) {
            columnSpeeds.push(i === 1 ? 0.12 : 0.25);
          } else if (numColumns === 4) {
            // Columns 2 & 4 (1-based) use the slower offset; 1 & 3 move faster
            columnSpeeds.push(i === 1 || i === 3 ? 0.12 : 0.25);
          } else {
            columnSpeeds.push(0);
          }
        }

        parallaxState = {
          gridEl,
          items,
          numColumns,
          columnSpeeds,
          sectionTop,
          gridHeight: rect.height,
        };
      }

      let parallaxCleared = false;

      /**
       * The last row's upward shift leaves empty space under the grid, so pull
       * the following content up. Set once per layout (not per scroll frame, which
       * would resize the page mid-scroll): use the shift of the slowest last-row
       * column at the moment the grid's bottom enters the viewport, so content
       * never overlaps the cards while they are visible.
       */
      function applyGridMargin() {
        if (!parallaxState || parallaxCleared) return;
        const { items, numColumns, columnSpeeds, gridHeight } = parallaxState;

        const remainder = items.length % numColumns;
        const lastRowCount = remainder === 0 ? numColumns : remainder;
        const lastRowStart = items.length - lastRowCount;
        let slowestSpeed = Infinity;
        for (let index = lastRowStart; index < items.length; index++) {
          const speed = columnSpeeds[index % numColumns] ?? 0;
          if (speed > 0 && speed < slowestSpeed) slowestSpeed = speed;
        }

        const scrollIntoGrid = gridHeight - window.innerHeight;
        const shift =
          slowestSpeed !== Infinity && scrollIntoGrid > 0
            ? -scrollIntoGrid * slowestSpeed
            : 0;
        gridEl.style.marginBottom = shift < 0 ? `${shift.toFixed(2)}px` : "";
      }

      function applyParallaxScroll() {
        parallaxTicking = false;
        if (!parallaxEnabled) {
          clearParallaxStyles();
          return;
        }
        if (!parallaxState) return;
        if (prefersReducedMotion) {
          clearParallaxStyles();
          return;
        }

        const width =
          window.innerWidth || document.documentElement.clientWidth || 0;
        if (width < parallaxMinWidth || parallaxState.numColumns === 1) {
          if (!parallaxCleared) {
            clearParallaxStyles();
            parallaxCleared = true;
          }
          return;
        }
        parallaxCleared = false;

        const scrollY = window.scrollY || window.pageYOffset || 0;
        const baseOffset = scrollY - parallaxState.sectionTop;
        const { items, numColumns, columnSpeeds } = parallaxState;

        items.forEach((item, index) => {
          const colIndex = numColumns > 0 ? index % numColumns : 0;
          const speed = columnSpeeds[colIndex] ?? 0;
          const offset = -baseOffset * speed;
          item.style.transform = offset
            ? `translateY(${offset.toFixed(2)}px)`
            : "";
        });
      }

      function onParallaxScroll() {
        if (!parallaxTicking && parallaxState) {
          parallaxTicking = true;
          requestAnimationFrame(applyParallaxScroll);
        }
      }

      const applyParallax = () => {
        updateParallaxLayout();
        parallaxCleared = false;
        applyParallaxScroll();
        applyGridMargin();
      };

      const setupParallaxListeners = () => {
        window.removeEventListener("scroll", onParallaxScroll, {
          passive: true,
        });
        window.removeEventListener("resize", onParallaxResize);
        window.addEventListener("scroll", onParallaxScroll, { passive: true });
        window.addEventListener("resize", onParallaxResize);
      };

      let parallaxLastWidth = window.innerWidth;
      function onParallaxResize() {
        // Height-only resizes are the mobile toolbar showing/hiding mid-scroll
        if (window.innerWidth === parallaxLastWidth) return;
        parallaxLastWidth = window.innerWidth;
        applyParallax();
      }

      const refreshScrollTriggers = () => {
        if (window.SOJ_SMOOTH_SCROLL?.scheduleRefreshDimensions) {
          window.SOJ_SMOOTH_SCROLL.scheduleRefreshDimensions();
        } else if (typeof window.ScrollTrigger !== "undefined") {
          requestAnimationFrame(() => {
            window.ScrollTrigger.refresh();
          });
        }
      };

      const handleError = (error) => {
        console.error("[BLOCK] projects filter error:", error);
        list.innerHTML = "";
        setEmptyState(false);
        refreshScrollTriggers();
      };

      const handleChange = () => {
        if (isResettingFilters) {
          return;
        }

        updateClearButtonVisibility();

        const spaceValue = spaceSelect ? spaceSelect.value : "";
        const styleValue = styleSelect ? styleSelect.value : "";
        const collectionValue = collectionSelect ? collectionSelect.value : "";

        const formData = new FormData();
        formData.append("action", action);
        formData.append("nonce", nonce);
        formData.append("space", spaceValue);
        formData.append("style", styleValue);
        formData.append("collection", collectionValue);
        formData.append("projects_source", results.dataset.projectsSource || "all");
        formData.append("project_ids", results.dataset.projectIds || "");

        results.classList.add("is-loading");

        fetch(ajaxUrl, {
          method: "POST",
          credentials: "same-origin",
          body: formData,
        })
          .then((response) => {
            if (!response.ok) {
              throw new Error(
                `Network response was not ok (${response.status})`,
              );
            }
            return response.json();
          })
          .then((data) => {
            if (!data || !data.success) {
              throw new Error(data?.data || "Unexpected response");
            }

            const html = data.data.html || "";
            const gsap = window.gsap;

            const applyNewContent = () => {
              list.innerHTML = html;
              setEmptyState(Boolean(data.data.found && html.trim().length > 0));
              if (typeof window.initCoverMediaSetup === "function") {
                window.initCoverMediaSetup();
              }
              applyParallax();
              refreshScrollTriggers();
            };

            if (gsap && list.hasChildNodes()) {
              gsap.to(list, {
                opacity: 0,
                duration: 0.25,
                ease: "power2.in",
                onComplete: () => {
                  applyNewContent();
                  gsap.fromTo(
                    list,
                    { opacity: 0 },
                    { opacity: 1, duration: 0.4, ease: "power2.out" },
                  );
                },
              });
            } else {
              applyNewContent();
            }
          })
          .catch(handleError)
          .finally(() => {
            results.classList.remove("is-loading");
          });
      };

      if (spaceSelect) {
        spaceSelect.addEventListener("change", handleChange);
      }
      if (styleSelect) {
        styleSelect.addEventListener("change", handleChange);
      }
      if (collectionSelect) {
        collectionSelect.addEventListener("change", handleChange);
      }
      if (clearFiltersButton) {
        clearFiltersButton.addEventListener("click", () => {
          isResettingFilters = true;

          if (spaceSelect) {
            spaceSelect.value = initialFilterValues.space;
          }
          if (styleSelect) {
            styleSelect.value = initialFilterValues.style;
          }
          if (collectionSelect) {
            collectionSelect.value = initialFilterValues.collection;
          }

          document.dispatchEvent(new CustomEvent("soj-update-filter-labels"));

          isResettingFilters = false;
          updateClearButtonVisibility();
          handleChange();
        });
      }

      updateClearButtonVisibility();

      if (parallaxEnabled) {
        applyParallax();
        setupParallaxListeners();
      } else {
        clearParallaxStyles();
      }

      let lastWidth = window.innerWidth;
      const onResize = () => {
        const w = window.innerWidth;
        const crossedBreakpoint =
          (lastWidth >= 1024 && w < 1024) ||
          (lastWidth < 1024 && w >= 1024) ||
          (lastWidth > 1920 && w <= 1920) ||
          (lastWidth <= 1920 && w > 1920);
        lastWidth = w;
        if (crossedBreakpoint) {
          if (parallaxEnabled) {
            applyParallax();
          }
          document.dispatchEvent(new CustomEvent("soj-update-filter-labels"));
        }
      };
      window.addEventListener("resize", onResize);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initProjects);
  } else {
    initProjects();
  }
})();
